import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import CardsTable from "@/components/CardsTable";
import ViewField from "@/components/Input/ViewField";
import ScoutReportsContainer from "@/components/ScoutReportsContainer";
import ValuationsPage from "@/pages/Valuations";
import ProspectPlayersReportsPage from "../../ProspectPlayersReports";
// import NotesSection from "@/components/NotesSection";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import VLFModal from "@/components/Modal";
import HandleCareerModal from "@/pages/Scouting/Players/Player/handleCareer";

import {AttachmentAPI} from "@/api/attachment";
import {PlayerAPI} from "@/api/player";
import {ScoutReportAPI} from "@/api/scout_report";

import ArrowBack from "@mui/icons-material/ArrowBack";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment} from "@/utils/functions";

import "./index.scss";
import {useTranslation} from "react-i18next";

const ScoutingPlayerPage = (props) => {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [player, setPlayer] = useState({});
	const [playerReports, setPlayerReports] = useState([]);

	const [idAttachment, setIdAttachment] = useState();
	const [attachments, setAttachments] = useState([]);

	const [initialData, setInitialData] = useState();

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openCareerDetail, setOpenCareerDetail] = useState(false);
	const [openHandleAttachmentConfirm, setOpenHandleAttachmentConfirm] =
		useState(false);

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			response.career = response.career?.map((c) => {
				if (c.id?.startsWith("00000000")) {
					c.readable = true;
				}
				return c;
			});

			// controllo eventuali righe doppie per stagione-squadra-categoria
			const seen = new Set();
			response.career = response.career.filter((obj) => {
				const key = `${obj.season?.name}-${obj.club?.name}-${obj.playerCategory?.name}`;
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});
			setPlayer(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const getPlayerReports = async () => {
		try {
			const response = await ScoutReportAPI.getByPlayerId(id);
			setPlayerReports(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const getPlayerAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("player", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const deletePlayer = async () => {
		setLoading(true);
		try {
			await PlayerAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/scouting/players");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};
	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(idAttachment);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleAttachmentConfirm(false);
			getPlayerAttachment();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getPlayer();
		getPlayerReports();
		getPlayerAttachment();
	}, []);

	const handleCareerDetail = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				...data,
				playerId: player?.id,
				seasonId: data?.careerSeason?.value,
				clubId: data?.careerClub?.value,
				playerCategoryId: data?.careerCategory?.value,
			};

			id
				? await PlayerAPI.updateCareer(player?.id, id, body)
				: await PlayerAPI.createCareer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			setOpenCareerDetail(false);
			setInitialData();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const deleteCareerDetail = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteCareer(player?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const goBack = () => {
		navigate("/scouting/players");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate("/scouting/players/" + player?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const mainConfiguration = [
		{
			value: player?.firstName,
			label: t("name"),
			size: "col-6 col-lg-4",
		},
		{
			value: player?.lastName,
			label: "Cognome",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.dateOfBirth
				? moment(player?.dateOfBirth).format(dateFormat)
				: null,
			label: "Data di nascita",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.height ? player.height + " cm" : "",
			label: "Altezza",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.weight ? player.weight + " cm" : "",
			label: "Peso",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.goalkeeper ? "Portiere" : player?.primaryRole,
			label: "Ruolo",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.favoriteHand,
			label: "Mano",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.favoriteFoot,
			label: "Piede",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.fatherHeight ? player.fatherHeight + " cm" : "",
			label: "Altezza padre",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.motherHeight ? player.motherHeight + " cm" : "",
			label: "Altezza madre",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.notes,
			label: "Note",
			size: "col-6 col-lg-4",
		},
	];

	const careerColumns = [
		{
			label: "Stagione",
			key: "season",
			customRender: (item) => item.season?.name,
		},
		{
			label: "Squadra",
			key: "club",
			customRender: (item) => item.club?.name,
		},
		{
			label: "Categoria",
			key: "playerCategory",
			customRender: (item) => item.playerCategory?.name,
		},
	];

	const breadcrumbsElements = [
		{label: "Giocatori", path: "/scouting/players"},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					player ? (player.firstName || "") + " " + (player.lastName || "") : ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container scouting-player-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<CardsTable
							title="Carriera"
							fullHeight={false}
							middleHeight
							configurationColumns={careerColumns}
							data={player?.career}
							actions={[
								{
									icon: <ControlPointIcon />,
									onClick: () => setOpenCareerDetail(true),
								},
							]}
							handleEdit={(item) => {
								setInitialData(Object.assign({}, item));
								setOpenCareerDetail(true);
							}}
							handleDelete={(item) => {
								deleteCareerDetail(item.id);
							}}
							defaultSortedField="dateEvent"
							defaultSortedOrder="descend"
						/>
					</div>
				</div>
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<div className="table-title px-1">Segnalazioni</div>
						<ScoutReportsContainer
							reports={playerReports}
							playerId={id}
							fullHeight={false}
							grouped={false}
							breadcrumbs={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<div className="table-title px-1">Valutazioni</div>
						<ValuationsPage external expanded={false} playerId={id} />
					</div>
				</div>
				{/* <NotesSection
					type="player_match"
					notes={player?.matchesNotes}
					chargeData={false}
				/> */}
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Allegati"
							content={
								<AttachmentsContainer
									entityId={id}
									entityType="player"
									attachments={attachments}
									handleAdd={() => getPlayerAttachment()}
									handleDownload={handleDownload}
									handleDelete={(id) => {
										setIdAttachment(id);
										setOpenHandleAttachmentConfirm(true);
									}}
									handleNotification={handleNotification}
								/>
							}
						/>
						<VLFModal
							open={openHandleAttachmentConfirm}
							title="Eliminazione"
							content={
								<div className="mt-2">
									Sei sicuro di voler eliminare questo elemento?
								</div>
							}
							confirm={() => deleteAttachment()}
							close={() => setOpenHandleAttachmentConfirm(false)}
							confirmLabel="Conferma"
						/>
					</div>
				</div>
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={() => {
						deletePlayer();
					}}
					close={() => setOpenHandleConfirm(false)}
					confirmLabel="Conferma"
				/>
				{openCareerDetail ? (
					<HandleCareerModal
						open={openCareerDetail}
						confirm={handleCareerDetail}
						close={() => {
							setOpenCareerDetail(false);
							setInitialData();
						}}
						initialData={initialData}
						handleNotification={handleNotification}
					/>
				) : null}
			</div>
		</Page>
	);
};

export default ScoutingPlayerPage;
